import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { MyShift, Shift, ShiftCover, CoverRequest, SwapOffer } from '@keetcheck/shared';
import { theme } from '../theme.js';
import { useAuth, useAccess } from '../context/AuthContext.js';
import { useMyShifts, useShifts, useCoverRequests, useSwaps, useActivities, useOrgMembers } from '../lib/queries.js';
import { SwapStatusPill } from './SwapPages.js';
import { ActivitiesList, ACTIVITY_LABEL } from './ActivityPages.js';
import { ScratchNudge } from './ScratchPages.js';
import { CapabilityStrip, CoverStatusPill, Pill } from '../components/CoverBits.js';
import { PageShell } from '../components/PageShell.js';
import { Icon } from '../components/Icon.js';
import { TeamChip } from '../components/RosterGrid.js';
import { LeaveCard, leaveLabel } from '../components/LeaveCard.js';
import { CalendarCard } from '../components/CalendarCard.js';
import {
  AS_LABEL, KIND_LABEL, timeRange, dayLabel, localDay, isDayShift, addDays, shortDate, currentDutyMonday, rangeLabel,
} from '../lib/shifts.js';

type Tab = 'mine' | 'week' | 'cover' | 'activities';
const TABS: readonly Tab[] = ['mine', 'week', 'cover', 'activities'];

/** My upcoming shifts, and who's on this week (everyone can see both). */
export function ShiftsPage() {
  // The tab lives in the address (replaced, not pushed), so coming back from
  // a cover request or activity lands on the tab it was opened from.
  const [params, setParams] = useSearchParams();
  const asked = params.get('tab') as Tab | null;
  const tab: Tab = asked && TABS.includes(asked) ? asked : 'mine';
  const setTab = (t: Tab) => setParams((ps) => {
    const next = new URLSearchParams(ps);
    if (t === 'mine') next.delete('tab'); else next.set('tab', t);
    return next;
  }, { replace: true });
  const { user } = useAuth();
  const { data: open = [] } = useCoverRequests(user?.orgId ?? '', 'open');
  const { data: swaps = [] } = useSwaps(user?.orgId ?? '');
  const canHelp = open.filter((r) => r.canTake).length + swaps.filter((x) => x.role === 'target' && x.status === 'pending').length;
  return (
    <PageShell>
      <div style={{
        padding: '12px 16px', background: theme.surface, borderBottom: `1px solid ${theme.border}`,
        position: 'sticky', top: 0, zIndex: 5,
      }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: theme.text, marginBottom: 10 }}>Shifts</div>
        <div role="tablist" style={{ display: 'flex', gap: 4, background: theme.surface2, padding: 3, borderRadius: 10 }}>
          {([['mine', 'Mine'], ['week', 'Who’s on'], ['cover', 'Cover'], ['activities', 'Activities']] as const).map(([id, label]) => (
            <button key={id} role="tab" aria-selected={tab === id} onClick={() => setTab(id)} style={{
              flex: 1, padding: '7px 0', borderRadius: 8, border: 'none', cursor: 'pointer', fontFamily: theme.font,
              fontSize: 13, fontWeight: 700,
              background: tab === id ? theme.surface : 'transparent',
              color: tab === id ? theme.text : theme.textMuted,
              boxShadow: tab === id ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
            }}>
              {label}
              {id === 'cover' && canHelp > 0 && (
                <span style={{
                  marginLeft: 6, fontSize: 11, fontWeight: 800, padding: '1px 6px', borderRadius: 999,
                  background: theme.status.warn.dot, color: '#fff',
                }}>{canHelp}</span>
              )}
            </button>
          ))}
        </div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', background: theme.bg, paddingBottom: 24 }}>
        {tab === 'mine' ? <MyShifts /> : tab === 'week' ? <WhosOn /> : tab === 'cover' ? <CoverList /> : <ActivitiesList />}
      </div>
    </PageShell>
  );
}

// ── My shifts ──────────────────────────────────────────────────

function MyShifts() {
  const nav = useNavigate();
  const { user } = useAuth();
  // Leaders and admins can look at a member's shifts — mostly to ask for
  // cover for someone who doesn't use the app.
  // Whose shifts these are lives in the address (?for=), replaced in place,
  // so a refresh or coming back shows the same person — and "Show my shifts"
  // really clears it.
  const [params, setParams] = useSearchParams();
  const forUser = params.get('for');
  const setForUser = (id: string | null) => setParams((ps) => {
    const next = new URLSearchParams(ps);
    if (id) next.set('for', id); else next.delete('for');
    return next;
  }, { replace: true });
  const { data: all = [], isLoading, error } = useMyShifts(forUser);
  // "Pick yours" ops slots are for the member to choose themselves.
  const shifts = forUser ? all.filter((s) => s.as !== 'ops_team') : all;
  const picker = <MemberPicker value={forUser} onChange={setForUser} />;
  const other = forUser ? <OtherMemberNote userId={forUser} /> : null;
  if (isLoading) return <>{picker}<Empty>Loading…</Empty></>;
  // Not allowed to look at them (only their team's leader or deputy, or an
  // admin, can): say so, instead of their header around an empty list.
  if (forUser && error) {
    return (
      <>
        {picker}
        <Empty>
          You can’t see their shifts — only an admin or their team’s leader or deputy can.
          <br />
          <button onClick={() => setForUser(null)} style={{ ...smallBtn, marginTop: 12 }}>Show my shifts</button>
        </Empty>
      </>
    );
  }
  if (!shifts.length) {
    return forUser
      ? <>{picker}{other}<Empty>Nothing rostered for them in the coming weeks.</Empty></>
      : <>{picker}<ScratchNudge orgId={user?.orgId ?? ''} /><Empty>Nothing rostered for you in the coming weeks.<br />If that looks wrong, ask an admin to check the roster.</Empty></>;
  }
  const weeks = [...new Set(shifts.map((s) => s.periodStart))];
  // Shifts at the same time (e.g. duty crew + UDO) show as one row.
  const slotsOf = (list: MyShift[]) => {
    const bySlot = new Map<string, MyShift[]>();
    for (const s of list) bySlot.set(s.startsAt, [...(bySlot.get(s.startsAt) ?? []), s]);
    return [...bySlot.values()];
  };
  const clashes = slotsOf(shifts.filter((s) => s.as !== 'ops_team')).filter((g) => g.some((s) => s.clashesWith.length > 0)).length;

  return (
    <>
      {picker}
      {other ?? <><MyLeaveNote /><ScratchNudge orgId={user?.orgId ?? ''} /></>}
      {clashes > 0 && (
        <div style={{ ...banner, background: theme.status.danger.bg, color: theme.status.danger.fg }}>
          <strong>{forUser ? 'They’re' : 'You’re'} rostered twice for {clashes} shift{clashes > 1 ? 's' : ''}.</strong> Tap <em>Can't make it</em> on one side to ask for cover.
        </div>
      )}
      {weeks.map((ps) => {
        const inWeek = shifts.filter((s) => s.periodStart === ps);
        const opsTeam = inWeek.filter((s) => s.as === 'ops_team');
        const definite = inWeek.filter((s) => s.as !== 'ops_team');
        return (
          <section key={ps}>
            <WeekHeader>Week of {shortDate(ps)}</WeekHeader>
            {slotsOf(definite).map((group) => <MyShiftRow key={group[0]!.id} shifts={group} forUser={forUser} />)}
            {opsTeam.length > 0 && !forUser && (
              <button onClick={() => nav(`/ops?week=${ps}`)} style={{
                ...rowBox, width: '100%', textAlign: 'left', border: 'none', borderBottom: `1px solid ${theme.border}`,
                cursor: 'pointer', fontFamily: theme.font, color: theme.textDim, fontSize: 13,
              }}>
                <Icon name="people" size={16} style={{ color: theme.textMuted, flexShrink: 0 }} />
                <span style={{ flex: 1 }}>
                  Your team is on <strong>ops support</strong> — {opsTeam.length} shift{opsTeam.length > 1 ? 's' : ''} still open, one person each.
                  {' '}<span style={{ color: theme.accent, fontWeight: 700 }}>Pick yours</span>
                </span>
                <Icon name="chevron" size={16} style={{ color: theme.textMuted }} />
              </button>
            )}
          </section>
        );
      })}
    </>
  );
}

/** "You're on leave until …" on my own shifts. */
function MyLeaveNote() {
  const { user } = useAuth();
  const { me } = useAccess();
  const { data: members = [] } = useOrgMembers(user?.orgId ?? '');
  const l = members.find((m) => m.userId === me?.id)?.leave;
  if (!l) return null;
  return (
    <div style={{ ...banner, background: theme.status.warn.bg, color: theme.status.warn.fg }}>
      <strong>{leaveLabel(l)}.</strong> Shifts during your leave don't need cover — you're not counted on the crew.
    </div>
  );
}

/** "Showing: Me / a member" — only for admins and team leaders/deputies. */
function MemberPicker({ value, onChange }: { value: string | null; onChange: (id: string | null) => void }) {
  const { user } = useAuth();
  const { me, isAdmin, ledTeam } = useAccess();
  const { data: members = [] } = useOrgMembers(isAdmin || ledTeam ? user?.orgId ?? '' : '');
  if (!isAdmin && !ledTeam) return null;
  const mine = members.filter((m) => m.userId !== me?.id && (isAdmin || m.teamId === ledTeam?.id));
  if (!mine.length) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px 0' }}>
      <label htmlFor="shifts-for" style={{ fontSize: 12, fontWeight: 700, color: theme.textMuted }}>Showing</label>
      <select id="shifts-for" value={value ?? ''} onChange={(e) => onChange(e.target.value || null)} style={{
        flex: 1, minWidth: 0, padding: '6px 8px', borderRadius: 8, border: `1px solid ${theme.border}`,
        background: theme.surface, color: theme.text, fontFamily: theme.font, fontSize: 13,
      }}>
        <option value="">My shifts</option>
        {mine.map((m) => (
          <option key={m.userId} value={m.userId}>
            {m.displayName}{[m.hasApp ? null : 'no app', m.leave?.active ? (m.leave.endsOn ? 'on leave' : 'paused') : null].filter(Boolean).map((x) => ` (${x})`).join('')}
          </option>
        ))}
      </select>
    </div>
  );
}

function OtherMemberNote({ userId }: { userId: string }) {
  const { user } = useAuth();
  const { data: members = [] } = useOrgMembers(user?.orgId ?? '');
  const m = members.find((x) => x.userId === userId);
  if (!m) return null;
  return (
    <>
    <div style={{ ...banner, background: theme.surface2, color: theme.textDim }}>
      {m.displayName}’s shifts.{' '}
      {m.hasApp
        ? 'They use the app, so they’re usually best placed to ask for cover themselves.'
        : 'They don’t use the app — tap Can’t make it to ask for cover for them. You’ll get the updates.'}
    </div>
    <div style={{ margin: '0 16px 8px', borderRadius: 12, overflow: 'hidden', border: `1px solid ${theme.border}` }}>
      <LeaveCard orgId={user?.orgId ?? ''} userId={m.userId} name={m.displayName} />
    </div>
    {!m.hasApp && <CalendarCard orgId={user?.orgId ?? ''} userId={m.userId} name={m.displayName} />}
    </>
  );
}

function MyShiftRow({ shifts, forUser = null }: { shifts: MyShift[]; forUser?: string | null }) {
  const nav = useNavigate();
  const s = shifts[0]!;
  const clash = shifts.some((x) => x.clashesWith.length > 0);
  const ended = new Date(s.endsAt) <= new Date();
  return (
    <div style={{ ...rowBox, alignItems: 'flex-start', ...(clash ? { boxShadow: `inset 3px 0 0 ${theme.status.danger.dot}` } : {}) }}>
      <div style={{ width: 86, flexShrink: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: theme.text }}>{dayLabel(s.startsAt)}</div>
        <div style={{ fontSize: 12, color: theme.textMuted }}>{timeRange(s.startsAt, s.endsAt)}</div>
      </div>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {shifts.map((x, i) => (
            <span key={x.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {i > 0 && <span style={{ color: theme.textMuted }}>+</span>}
              <span style={{ fontSize: 14, fontWeight: 600, color: theme.text }}>
                {x.as === 'activity'
                  ? x.activity!.title
                  : x.as === 'cover'
                    ? `${x.coverFor?.swapId ? 'Swap with' : 'Covering'} ${x.coverFor?.displayName ?? ''} · ${KIND_LABEL[x.kind]}`
                    : AS_LABEL[x.as]}
              </span>
              {x.as === 'duty_crew' && <TeamChip name={x.teamName} />}
            </span>
          ))}
          {s.as === 'activity'
            ? s.activity!.title !== ACTIVITY_LABEL[s.activity!.kind] && <span style={{ fontSize: 11, color: theme.textMuted }}>{ACTIVITY_LABEL[s.activity!.kind].toLowerCase()}</span>
            : <span style={{ fontSize: 11, color: theme.textMuted }}>{isDayShift(s.startsAt) ? 'day' : 'night'}</span>}
        </div>
        {s.holiday && <div style={{ fontSize: 12, color: theme.status.service.fg }}>{s.holiday}</div>}
        {clash && <div style={{ fontSize: 12, fontWeight: 700, color: theme.status.danger.fg }}>Clash — {forUser ? 'they' : 'you'} can't do both</div>}
        {shifts.every((x) => x.onLeave) && <div><Pill tone="warn">On leave</Pill></div>}
        {shifts.map((x) => x.myCover && <MyCoverLine key={`c-${x.id}`} cover={x.myCover} multi={shifts.length > 1} label={AS_LABEL[x.as]} />)}
        {!ended && !shifts.every((x) => x.onLeave) && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {shifts.map((x) => x.as === 'activity'
              ? <button key={x.id} onClick={() => nav(`/activity/${x.activity!.id}`)} style={smallBtn}>Details</button>
              : x.as === 'cover'
              ? <button key={x.id} onClick={() => nav(x.coverFor!.swapId ? `/swap/${x.coverFor!.swapId}` : `/cover/${x.coverFor!.requestId}`)} style={smallBtn}>Details</button>
              : !x.myCover && (
                <span key={x.id} style={{ display: 'contents' }}>
                  <button onClick={() => nav(`/cover/new?shift=${encodeURIComponent(x.id)}${forUser ? `&for=${encodeURIComponent(forUser)}` : ''}`)} style={smallBtn}>
                    Can't make it{shifts.length > 1 ? ` (${AS_LABEL[x.as]})` : ''}
                  </button>
                  {!forUser && (
                    <button onClick={() => nav(`/swap/new?shift=${encodeURIComponent(x.id)}`)} style={smallBtn}>
                      Swap{shifts.length > 1 ? ` (${AS_LABEL[x.as]})` : ''}
                    </button>
                  )}
                </span>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** "Covered · Sam 6pm–10pm · gap 10pm–6am" under one of my shifts. */
function MyCoverLine({ cover, multi, label }: { cover: ShiftCover; multi: boolean; label: string }) {
  const nav = useNavigate();
  return (
    <button onClick={() => nav(cover.swapId ? `/swap/${cover.swapId}` : `/cover/${cover.requestId}`)} style={{
      textAlign: 'left', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: theme.font,
      display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: 12, color: theme.textDim,
    }}>
      {cover.swapId ? <Pill tone="service">Swapped</Pill> : <CoverStatusPill status={cover.status} partly={cover.takers.length > 0} />}
      {multi && <span>{label}:</span>}
      {cover.takers.map((t) => <span key={t.startsAt}>{t.displayName} {timeRange(t.startsAt, t.endsAt)}</span>)}
      {cover.gaps.length > 0 && <span style={{ color: theme.status.warn.fg, fontWeight: 600 }}>gap {cover.gaps.map((g) => timeRange(g.startsAt, g.endsAt)).join(', ')}</span>}
      <Icon name="chevron" size={12} />
    </button>
  );
}

// ── Cover ──────────────────────────────────────────────────────

function CoverList() {
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';
  const { data: open = [], isLoading } = useCoverRequests(orgId, 'open');
  const { data: mine = [] } = useCoverRequests(orgId, 'mine');
  const { data: swaps = [] } = useSwaps(orgId);
  const toAnswer = swaps.filter((x) => x.role === 'target' && x.status === 'pending');
  const otherSwaps = swaps.filter((x) => !(x.role === 'target' && x.status === 'pending'));
  const others = open.filter((r) => !r.mine);
  const help = others.filter((r) => r.canTake);
  const rest = others.filter((r) => !r.canTake);
  return (
    <>
      {toAnswer.length > 0 && <WeekHeader>Swap offers for you</WeekHeader>}
      {toAnswer.map((x) => <SwapRow key={x.id} swap={x} />)}
      <WeekHeader>Can you help?</WeekHeader>
      {isLoading && <Empty>Loading…</Empty>}
      {!isLoading && help.length === 0 && <Empty>Nothing you can cover right now. Thanks for checking!</Empty>}
      {help.map((r) => <CoverRow key={r.id} req={r} />)}
      {mine.some((r) => !r.swapId) && <WeekHeader>Your cover requests</WeekHeader>}
      {mine.filter((r) => !r.swapId).map((r) => <CoverRow key={r.id} req={r} />)}
      {otherSwaps.length > 0 && <WeekHeader>Your swaps</WeekHeader>}
      {otherSwaps.map((x) => <SwapRow key={x.id} swap={x} />)}
      {rest.length > 0 && <WeekHeader>Other open requests</WeekHeader>}
      {rest.map((r) => <CoverRow key={r.id} req={r} />)}
    </>
  );
}

function SwapRow({ swap: x }: { swap: SwapOffer }) {
  const nav = useNavigate();
  const other = x.role === 'proposer' ? x.target.displayName : x.proposer.displayName;
  const iTake = x.role === 'target' ? x.give : x.want;
  return (
    <button onClick={() => nav(`/swap/${x.id}`)} style={{
      ...rowBox, width: '100%', textAlign: 'left', border: 'none', borderBottom: `1px solid ${theme.border}`,
      cursor: 'pointer', fontFamily: theme.font,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: theme.text }}>Swap with {other}</span>
          <SwapStatusPill status={x.status} />
        </div>
        <div style={{ fontSize: 13, color: theme.textDim }}>
          You take on: {iTake ? `${KIND_LABEL[iTake.kind]} ${rangeLabel(iTake.startsAt, iTake.endsAt)}` : `${other} picks`}
        </div>
      </div>
      <Icon name="chevron" size={16} style={{ color: theme.textMuted }} />
    </button>
  );
}

function CoverRow({ req }: { req: CoverRequest }) {
  const nav = useNavigate();
  const covering = req.takes.some((t) => t.isMine);
  return (
    <button onClick={() => nav(`/cover/${req.id}`)} style={{
      ...rowBox, width: '100%', textAlign: 'left', border: 'none', borderBottom: `1px solid ${theme.border}`,
      cursor: 'pointer', fontFamily: theme.font,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: theme.text }}>{KIND_LABEL[req.kind]}</span>
          <CoverStatusPill status={req.status} partly={req.takes.length > 0} />
          {covering && <Pill tone="service">You're covering</Pill>}
        </div>
        <div style={{ fontSize: 13, color: theme.textDim }}>{rangeLabel(req.startsAt, req.endsAt)}</div>
        <div style={{ fontSize: 12, color: theme.textMuted }}>
          {req.mine ? 'Your request' : req.requester.displayName}
          {req.gaps.length > 0 && req.status === 'open'
            ? ` · ${req.gaps.length === 1 ? timeRange(req.gaps[0]!.startsAt, req.gaps[0]!.endsAt) : `${req.gaps.length} parts`} still open`
            : ''}
        </div>
      </div>
      <Icon name="chevron" size={16} style={{ color: theme.textMuted }} />
    </button>
  );
}

// ── Who's on ───────────────────────────────────────────────────

function WhosOn() {
  const nav = useNavigate();
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';
  const [monday, setMonday] = useState(currentDutyMonday());
  // A week's shifts run to Monday 6am, plus a Monday-holiday day shift that
  // ends the long weekend — so fetch a day extra and keep this duty week's.
  const { data: all = [], isLoading } = useShifts(orgId, monday, addDays(monday, 8));
  const { data: acts = [] } = useActivities(orgId, `${monday}T00:00:00`, `${addDays(monday, 8)}T00:00:00`);
  const shifts = all.filter((s) => s.periodStart === monday);
  const days = [...new Set(shifts.map((s) => localDay(s.startsAt)))].sort();
  const isThisWeek = monday === currentDutyMonday();

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px' }}>
        <button onClick={() => setMonday(addDays(monday, -7))} aria-label="Previous week" style={navBtn}><Icon name="chevronL" size={16} /></button>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: theme.text }}>
            {isThisWeek ? 'This week' : `Week of ${shortDate(monday)}`}
          </div>
          <div style={{ fontSize: 12, color: theme.textMuted }}>Mon {shortDate(monday)} 6pm → Mon {shortDate(addDays(monday, 7))} 6am</div>
        </div>
        <button onClick={() => setMonday(addDays(monday, 7))} aria-label="Next week" style={navBtn}><Icon name="chevron" size={16} /></button>
      </div>
      {isLoading && <Empty>Loading…</Empty>}
      {!isLoading && !shifts.length && <Empty>Nothing rostered for this week yet.</Empty>}
      {days.map((day) => {
        const inDay = shifts.filter((s) => localDay(s.startsAt) === day);
        const dayShifts = inDay.filter((s) => isDayShift(s.startsAt));
        const nightShifts = inDay.filter((s) => !isDayShift(s.startsAt));
        return (
          <section key={day}>
            <WeekHeader>{dayLabel(inDay[0]!.startsAt)}{inDay.find((s) => s.holiday)?.holiday ? ` · ${inDay.find((s) => s.holiday)!.holiday}` : ''}</WeekHeader>
            {acts.filter((a) => !a.cancelled && localDay(a.startsAt) === day).map((a) => (
              <button key={a.id} onClick={() => nav(`/activity/${a.id}`)} style={{
                ...rowBox, width: '100%', textAlign: 'left', border: 'none', borderBottom: `1px solid ${theme.border}`,
                cursor: 'pointer', fontFamily: theme.font, background: theme.status.service.bg,
              }}>
                <div style={{ width: 86, flexShrink: 0, fontSize: 12, color: theme.status.service.fg, fontWeight: 700 }}>{timeRange(a.startsAt, a.endsAt)}</div>
                <div style={{ flex: 1, fontSize: 13, color: theme.text }}>
                  <strong>{a.title}</strong>{a.hostTeam ? <> · <TeamChip name={a.hostTeam.name} /> hosting</> : ''}
                </div>
              </button>
            ))}
            {dayShifts.length > 0 && <ShiftBlock label="Day" shifts={dayShifts} />}
            {nightShifts.length > 0 && <ShiftBlock label="Night" shifts={nightShifts} />}
          </section>
        );
      })}
    </>
  );
}

const ORDER: Shift['kind'][] = ['duty_crew', 'udo', 'udo_day', 'ops_support', 'activity'];

function ShiftBlock({ label, shifts }: { label: string; shifts: Shift[] }) {
  const sorted = [...shifts].sort((a, b) => ORDER.indexOf(a.kind) - ORDER.indexOf(b.kind));
  return (
    <div style={{ ...rowBox, alignItems: 'flex-start' }}>
      <div style={{ width: 86, flexShrink: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: theme.text }}>{label}</div>
        <div style={{ fontSize: 12, color: theme.textMuted }}>{timeRange(sorted[0]!.startsAt, sorted[0]!.endsAt)}</div>
      </div>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {sorted.map((s) => (
          <div key={s.id} style={{ fontSize: 13, color: theme.textDim }}>
            <span style={{ display: 'inline-block', minWidth: 78, fontWeight: 600, color: theme.text }}>{KIND_LABEL[s.kind]}</span>
            <Who shift={s} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function Who({ shift: s }: { shift: Shift }) {
  if (s.userId) {
    const c = s.cover;
    if (!c) return <span>{s.userName}</span>;
    return (
      <span>
        <span style={{ textDecoration: c.gaps.length === 0 ? 'line-through' : 'none' }}>{s.userName}</span>
        {c.takers.map((t) => <span key={t.startsAt} style={{ color: theme.status.ok.fg, fontWeight: 600 }}> → {t.displayName} {timeRange(t.startsAt, t.endsAt)}</span>)}
        {c.gaps.length > 0 && <span style={{ color: theme.status.danger.fg, fontWeight: 600 }}> · gap {c.gaps.map((g) => timeRange(g.startsAt, g.endsAt)).join(', ')}</span>}
      </span>
    );
  }
  if (s.kind === 'duty_crew' && s.crew) {
    return (
      <span>
        {s.teamName ? <TeamChip name={s.teamName} /> : <span style={{ fontWeight: 700, color: theme.text }}>Scratch crew</span>}
        <span style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 10px', fontSize: 12, marginTop: 4 }}>
          {s.crew.map((m) => (
            <span key={m.userId} style={{ color: m.status === 'on' ? theme.textDim : m.status === 'covered' || m.status === 'leave' ? theme.textMuted : theme.status.danger.fg }}>
              <span style={{ textDecoration: m.status === 'on' || m.status === 'leave' ? 'none' : 'line-through', opacity: m.status === 'leave' ? 0.7 : 1 }}>
                {m.displayName}{m.teamRole !== 'member' ? ` (${m.teamRole === 'leader' ? 'TL' : 'DTL'})` : ''}
              </span>
              {m.status === 'leave' && <span style={{ fontStyle: 'italic' }}> · on leave</span>}
              {m.coveredBy.map((c) => <span key={c.startsAt} style={{ color: theme.status.ok.fg, fontWeight: 600 }}> → {c.displayName}</span>)}
              {m.gaps.length > 0 && <span style={{ fontWeight: 600 }}> · gap</span>}
            </span>
          ))}
        </span>
        {s.capability && <span style={{ display: 'block', marginTop: 6 }}><CapabilityStrip cap={s.capability} compact /></span>}
      </span>
    );
  }
  if (s.teamId) {
    return (
      <span>
        <TeamChip name={s.teamName} />
        {s.kind === 'ops_support' && <span style={{ color: theme.status.warn.fg, fontWeight: 600 }}> · not picked yet</span>}
        {s.kind === 'duty_crew' && s.members && s.members.length > 0 && (
          <span style={{ display: 'block', fontSize: 12, color: theme.textMuted, marginTop: 2 }}>
            {s.members.map((m) => m.displayName + (m.teamRole !== 'member' ? ` (${m.teamRole === 'leader' ? 'TL' : 'DTL'})` : '')).join(', ')}
          </span>
        )}
      </span>
    );
  }
  return <span style={{ color: theme.status.warn.fg, fontWeight: 600 }}>{s.kind === 'duty_crew' ? 'Scratch crew' : 'Not picked'}</span>;
}

// ── bits ───────────────────────────────────────────────────────

function WeekHeader({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      padding: '8px 16px', fontSize: 11, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase',
      color: theme.textMuted, background: theme.bg, borderBottom: `1px solid ${theme.border}`,
    }}>{children}</div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div style={{ padding: '32px 24px', textAlign: 'center', fontSize: 13, color: theme.textMuted, lineHeight: 1.5 }}>{children}</div>;
}

const rowBox: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
  background: theme.surface, borderBottom: `1px solid ${theme.border}`,
};

const banner: React.CSSProperties = { margin: '12px 16px', padding: '10px 12px', borderRadius: 10, fontSize: 13, lineHeight: 1.4 };

const smallBtn: React.CSSProperties = {
  padding: '5px 10px', borderRadius: 8, border: `1px solid ${theme.border}`, background: theme.surface,
  fontSize: 12, fontWeight: 700, color: theme.textDim, cursor: 'pointer', fontFamily: theme.font,
};

const navBtn: React.CSSProperties = {
  width: 34, height: 34, borderRadius: 10, border: `1px solid ${theme.border}`, background: theme.surface,
  color: theme.text, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
};
